import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Brush,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  adminCostSummary,
  adminCostsByTenant,
  adminCostsByType,
  adminCostsByStep,
  adminCostsTimeseries,
  adminCostsTopRequests,
  adminListTenants,
} from "../api/client";
import type { CostWindow } from "../api/client";
import type {
  CostSummary, CostByTenant, CostByType, CostByStep, CostCoverage, AdminTenant,
  CostBucket, CostTimeseries, CostGranularity, CostTopRequest, CostTopRequestsResponse,
  CostCategoryShare,
} from "../api/types";
import {
  stepColorScale, stepLabel, CHART_PALETTE,
  CATEGORY_ORDER, categoryColor, categoryHint, categoryLabel,
} from "../lib/costSteps";
import {
  GRANULARITY_LABELS, bucketTickLabel, bucketTooltipLabel, granularityOptions, windowDays,
} from "../lib/costBuckets";
import { DateRangePicker } from "../components/DateRangePicker";
import { StatusBadge } from "../lib/status";

/** Preset di finestra. `days` serve solo a sapere in anticipo quali
 *  granularita' il backend accettera' (`granularityOptions`): la finestra
 *  vera resta quella che l'API riecheggia in `date_from`/`date_to`. */
const PERIODS: { value: string; label: string; days: number }[] = [
  { value: "1d", label: "Oggi", days: 1 },
  { value: "7d", label: "7 giorni", days: 7 },
  { value: "30d", label: "30 giorni", days: 30 },
  { value: "90d", label: "90 giorni", days: 90 },
  { value: "365d", label: "12 mesi", days: 365 },
  // Preset pluriennale: e' la domanda "come vedo i costi di questo cliente
  // dopo un anno?". Con `auto` si legge a MESI (36 barre): a settimane
  // sarebbero 157, illeggibili, e il confronto utile su un'orizzonte del
  // genere e' mese su mese.
  { value: "1095d", label: "3 anni", days: 1095 },
];

function daysOfPeriod(period: string): number {
  return PERIODS.find((p) => p.value === period)?.days ?? 30;
}

/**
 * Quanti ticket mostrare in classifica. Dieci risponde alla domanda "cosa e'
 * costato piu' del solito"; i tagli piu' larghi servono quando la finestra e'
 * ampia e la coda conta. Non esiste "tutti": il `LIMIT` e' cio' che tiene
 * l'ordinamento in SQL invece di scaricare la finestra nel browser (il tetto
 * lato server e' 100).
 */
const TOP_LIMITS = [10, 25, 50] as const;

/** Le granularita' richiedibili, nell'ordine del selettore (dal fine al grossolano). */
const GRANULARITY_CHOICES = ["auto", "hour", "day", "week", "month"] as const;

/**
 * Titolo della card del trend: dipende dalla granularita' RISOLTA, perche'
 * "Trend giornaliero" sopra barre orarie e' un'etichetta falsa (era il
 * difetto segnalato: un giorno solo mostrava una barra isolata "giornaliera").
 */
const TREND_TITLES: Record<CostGranularity, string> = {
  hour: "Trend per ora",
  day: "Trend giornaliero",
  week: "Trend settimanale",
  month: "Trend mensile",
};

/** Unita' al singolare per le didascalie ("usato ogni ora / giorno / mese"). */
const BUCKET_UNIT: Record<CostGranularity, string> = {
  hour: "ora",
  day: "giorno",
  week: "settimana",
  month: "mese",
};

/**
 * Costo e latenza possono essere `null` = "non lo sappiamo" (nessuna chiamata
 * con prezzo, o nessuna latenza registrata). Renderli come 0 e' esattamente il
 * difetto che stiamo eliminando: qui diventano un trattino.
 */
const NO_DATA = "—";

// Locale italiano: virgola per i decimali, non il punto — mai `toFixed` grezzo.
const _fmtEurCache = new Map<number, Intl.NumberFormat>();
function _fmtEur(digits: number): Intl.NumberFormat {
  let f = _fmtEurCache.get(digits);
  if (!f) { f = new Intl.NumberFormat("it-IT", { minimumFractionDigits: digits, maximumFractionDigits: digits }); _fmtEurCache.set(digits, f); }
  return f;
}

function fmtUsd(v: number | null | undefined, digits = 2) {
  if (v == null) return NO_DATA;
  // Un costo reale (>0) che arrotonda a "0,00" sembra gratis — esattamente
  // l'invariante che questo pannello non deve mai violare (vedi backend,
  // stessa regola sul NULL). Capita spesso per l'OCR a pagina (pochi
  // millesimi di euro): qui si espande la precisione invece di mentire.
  if (v > 0 && Number(v.toFixed(digits)) === 0) return `€${_fmtEur(4).format(v)}`;
  return `€${_fmtEur(digits).format(v)}`;
}

function fmtMs(v: number | null | undefined) {
  return v == null ? NO_DATA : `${v.toFixed(0)} ms`;
}

function fmtInt(v: number) {
  return v.toLocaleString("it-IT");
}

/**
 * Valore da tile KPI: la tile del DS (`.kpi-grid`, min 180px) non tiene
 * "22.585.300" a 2rem su una riga, quindi dai milioni in su il numero e'
 * compatto ("22,6 Mln") e quello esatto va nel `title`. Sotto il milione si
 * mostra intero: e' li' che la precisione serve davvero.
 */
const COMPACT_FROM = 1_000_000;
const fmtCompactIt = new Intl.NumberFormat("it-IT", { notation: "compact", maximumFractionDigits: 1 });
function fmtKpiInt(v: number): { text: string; title?: string } {
  if (Math.abs(v) < COMPACT_FROM) return { text: fmtInt(v) };
  return { text: fmtCompactIt.format(v), title: fmtInt(v) };
}

/**
 * Percentuale in locale italiano: la virgola. `toFixed(1)` scriverebbe
 * "51.3%" accanto a "€1,13" nella stessa tabella — l'utente ha gia' corretto
 * due volte il separatore decimale su questa pagina.
 */
const _fmtPctIt = new Intl.NumberFormat("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
function fmtPct(v: number) {
  return `${_fmtPctIt.format(v)}%`;
}

/**
 * `detail` di una risposta d'errore FastAPI, se e' un testo.
 *
 * Sui 400 di questa rotta il backend valorizza `detail` con una frase in
 * italiano (date spaiate, intervallo invertito, formato sbagliato, finestra
 * troppo ampia, `hour` su finestra troppo lunga): quella frase e' la migliore
 * spiegazione disponibile, quindi si mostra. Un 422 di validazione porta
 * invece una LISTA di oggetti `{loc,msg}` con messaggi in inglese: li' si
 * ritorna null e si usa il testo generico italiano.
 */
function detailDiErrore(body: string): string | null {
  try {
    const parsed = JSON.parse(body) as { detail?: unknown };
    return typeof parsed.detail === "string" && parsed.detail.trim() ? parsed.detail.trim() : null;
  } catch {
    return null;
  }
}

/**
 * Errore di rete -> frase per l'utente. `apiFetch` impacchetta il corpo
 * grezzo nel messaggio (`API 400: {"detail":"..."}`): mostrarlo cosi' com'e'
 * significa mettere del JSON in faccia a chi guarda i costi.
 */
export function messaggioErrore(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err ?? "");
  const m = /^API (\d{3}): ([\s\S]*)$/.exec(raw);
  if (!m) return raw || "Errore inatteso nel caricamento dei consumi.";
  const [, stato, corpo] = m;
  const detail = detailDiErrore(corpo);
  if (detail) return detail;
  if (stato === "422") return "Parametri della richiesta non validi: finestra o dettaglio non ammessi.";
  return `Errore ${stato} nel caricamento dei consumi.`;
}

/** Costo in tile KPI: sempre 2 decimali (mai notazione compatta), cosi' il
 * valore resta leggibile senza andare a capo qualunque sia l'importo. */
function fmtKpiUsd(v: number | null): { text: string; title?: string } {
  return { text: fmtUsd(v) };
}

/**
 * Costo di una riga aggregata, "onesto": se nessuna chiamata del gruppo e'
 * prezzata (`priced_call_count === 0`) il valore e' ignoto anche se l'API
 * dovesse emettere uno 0. Il backend emette gia' null; questa e' la cintura.
 */
function costOf(row: { total_cost_usd: number | null } & CostCoverage): number | null {
  if (row.priced_call_count === 0) return null;
  return row.total_cost_usd;
}

/** Stessa guardia per la latenza: zero misure = nessuna media, non "0 ms". */
function latencyOf(row: { avg_latency_ms: number | null } & CostCoverage): number | null {
  if (row.latency_sample_count === 0) return null;
  return row.avg_latency_ms;
}

/** Somma "onesta": ignora i null ma non li conta come 0 nel denominatore. */
function sumCost(rows: ({ total_cost_usd: number | null } & CostCoverage)[]) {
  return rows.reduce((s, r) => s + (costOf(r) ?? 0), 0);
}

/* ── Scomposizione del costo di un ticket ─────────────────────────────────────
 *
 * Da quando i turni dell'agente conversazionale portano il `request_id` della
 * pratica che ne nasce, il costo di un ticket conversazionale COMPRENDE il
 * dialogo che l'ha prodotto. E' il numero giusto per "quanto e' costato questo
 * risultato" e quello sbagliato per "costa piu' di quello arrivato via email",
 * dove un dialogo non c'e'. Quindi la pagina mostra entrambi: il totale, che
 * resta la colonna ordinata, e di che cosa e' fatto.
 *
 * Le categorie partizionano le chiamate lato backend (invariante 8), percio'
 * la somma dei segmenti E' il totale della riga: qui non si normalizza niente
 * a 100 e non si inventa un residuo.
 */

/** Costo di una voce, con la stessa cintura di `costOf`: zero chiamate
 *  prezzate = costo ignoto, mai 0 (il backend emette gia' null). */
function costOfShare(v: CostCategoryShare): number | null {
  return v.priced_call_count === 0 ? null : v.cost_usd;
}

/** Somma delle voci note. E' il denominatore delle percentuali dei segmenti:
 *  si usa questo e non il `total_cost_usd` della riga proprio perche' i due
 *  devono coincidere — se un giorno divergessero, la barra lo mostrerebbe
 *  invece di riscalarsi in silenzio su un totale diverso. */
function sumShares(voci: CostCategoryShare[]): number {
  return voci.reduce((s, v) => s + (costOfShare(v) ?? 0), 0);
}

/** Voci in ordine di presentazione (quello del backend), non in ordine di
 *  arrivo: i segmenti devono stare nello stesso posto su ogni riga, altrimenti
 *  due barre non si confrontano a colpo d'occhio. */
function shareInOrdine(voci: CostCategoryShare[]): CostCategoryShare[] {
  const pos = new Map(CATEGORY_ORDER.map((c, i) => [c as string, i]));
  return [...voci].sort(
    (a, b) => (pos.get(a.category) ?? 99) - (pos.get(b.category) ?? 99),
  );
}

/** `title` di una voce: euro esatti, chiamate, e cosa c'e' dentro la categoria. */
function titoloShare(v: CostCategoryShare): string {
  const costo = costOfShare(v);
  const euro = costo == null
    ? "costo ignoto (nessuna chiamata prezzata)"
    : `€${_fmtEur(6).format(costo)}`;
  return `${categoryLabel(v.category)}: ${euro} su ${fmtInt(v.call_count)} chiamate. ${categoryHint(v.category)}`;
}

/**
 * Barra proporzionale della scomposizione: un segmento per categoria, larghezza
 * pari alla sua quota del totale.
 *
 * Perche' la barra E le cifre, e non solo una delle due. La barra risponde a
 * "questo ticket e' caro per il dialogo o per la lavorazione?" senza leggere
 * numeri — che e' la domanda con cui si scorre una classifica. Le cifre
 * accanto restano perche' un tooltip non si vede in uno screenshot, non si
 * legge con la tastiera e non si somma a occhio: e la somma che torna e'
 * l'unica prova che la scomposizione non e' decorativa.
 *
 * Una voce a costo ignoto non ha segmento (non c'e' una quota da disegnare) ma
 * compare fra le cifre con il trattino: sparire dalla barra e dal testo
 * sarebbe costo tolto dalla pagina.
 */
function ComposizioneBar({ voci }: { voci: CostCategoryShare[] }) {
  const ordinate = shareInOrdine(voci);
  const totale = sumShares(ordinate);
  if (ordinate.length === 0) return <span className="text-muted">{NO_DATA}</span>;

  return (
    <div>
      <div className="progress-bar" style={{ display: "flex" }} aria-hidden="true">
        {ordinate.map((v) => {
          const costo = costOfShare(v);
          const quota = totale > 0 && costo != null ? (costo / totale) * 100 : 0;
          if (quota <= 0) return null;
          return (
            <div
              key={v.category}
              title={titoloShare(v)}
              style={{ width: `${quota}%`, background: categoryColor(v.category) }}
            />
          );
        })}
      </div>
      <div className="text-xs text-muted" style={{ marginTop: 4 }}>
        {ordinate.map((v, i) => (
          <span key={v.category} title={titoloShare(v)}>
            {i > 0 ? " · " : ""}
            {categoryLabel(v.category)} {fmtUsd(costOfShare(v))}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Legenda delle categorie presenti nella classifica: i colori dei segmenti
 *  vanno spiegati una volta sopra la tabella, non riga per riga. Elenca solo
 *  le categorie che compaiono davvero — una voce per una categoria assente
 *  sarebbe rumore. */
function ComposizioneLegenda({ categorie }: { categorie: string[] }) {
  const presenti = CATEGORY_ORDER.filter((c) => categorie.includes(c));
  if (presenti.length === 0) return null;
  return (
    <span className="text-xs text-muted" style={{ display: "inline-flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
      {presenti.map((c) => (
        <span key={c} title={categoryHint(c)} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <span style={{
            display: "inline-block", width: 10, height: 10, borderRadius: 2,
            background: categoryColor(c),
          }} />
          {categoryLabel(c)}
        </span>
      ))}
    </span>
  );
}

/**
 * Di cosa e' fatto il costo attribuito ai ticket, sotto la riga di KPI.
 *
 * Deliberatamente NON e' una tile in piu': l'utente ha gia' dovuto correggere
 * questa pagina perche' i KPI erano illeggibili, e quattro tile di categoria
 * strette 180px la riporterebbero la'. Qui la scomposizione sta su una riga
 * sola — barra piu' cifre piu' percentuali — e le tile restano quelle che
 * erano, cifre a due decimali comprese.
 *
 * Il totale di riferimento e' `ticket_cost_usd` (il numeratore del costo medio
 * per ticket), non `total_cost_usd`: cio' che non ha un ticket non ha nemmeno
 * una pratica di cui essere "il dialogo", e mescolarlo qui rifarebbe l'errore
 * che l'invariante 7 ha eliminato dalla media.
 */
function TicketCostComposition({ data }: { data: CostSummary }) {
  const voci = shareInOrdine(data.ticket_cost_by_category ?? []);
  // Con una sola categoria non c'e' niente da scomporre: la riga direbbe
  // "Analisi €1,13 (100,0%)" accanto a una tile che dice gia' €1,13, cioe'
  // ripeterebbe il KPI con dieci parole in piu'. La scomposizione ha senso
  // quando ci sono almeno due nature di costo da confrontare; nel caso a una
  // voce l'informazione resta nel `title` della tile e nella torta per step.
  if (voci.length < 2) return null;
  const totale = sumShares(voci);

  return (
    <div
      className="text-sm"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--space-3)",
        flexWrap: "wrap",
        margin: "calc(-1 * var(--space-3)) 0 var(--space-4)",
      }}
    >
      <span className="text-muted" style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-2)" }}>
        <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--indaco-gray-500)" }}>pie_chart</span>
        Di cosa è fatto il costo dei ticket ({fmtUsd(data.ticket_cost_usd)}):
      </span>
      {voci.map((v) => {
        const costo = costOfShare(v);
        const quota = totale > 0 && costo != null ? (costo / totale) * 100 : null;
        return (
          <span
            key={v.category}
            title={titoloShare(v)}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <span style={{
              display: "inline-block", width: 10, height: 10, borderRadius: 2,
              background: categoryColor(v.category),
            }} />
            <strong>{categoryLabel(v.category)}</strong>
            {fmtUsd(costo)}
            {quota == null ? "" : <span className="text-muted">({fmtPct(quota)})</span>}
          </span>
        );
      })}
    </div>
  );
}

/**
 * Istante ISO (con offset) -> giorno nel fuso dichiarato dall'API. Senza
 * `timeZone` il browser userebbe il proprio: un operatore fuori dall'Italia
 * (o un server in UTC) vedrebbe la finestra "1 set – 30 set" slittare di un
 * giorno.
 */
function fmtDay(iso: string, timeZone?: string) {
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric", timeZone });
  } catch { return iso; }
}

/* Le etichette delle chiavi di bucket (`splitBucketKey`, `bucketTickLabel`,
   `bucketTooltipLabel`) vivono in `lib/costBuckets.ts`: sono pure, servono a
   tre granularita' e sono coperte da test. Qui si importano. */

/** Nome del modulo (`tenant.modules`, es. `["rdi","ddt","conversazioni"]`) ->
 * etichetta del flusso. Stesse chiavi di `_FLOW_STEPS` nel backend. */
const FLOW_LABELS: Record<string, string> = {
  rdi: "RDI",
  ddt: "DDT",
  bsc: "BSC",
  rinascente: "Rinascente",
  deleghe: "Deleghe",
  conversazioni: "Agente conversazionale",
};

export function AdminCosts() {
  const [period, setPeriod] = useState("30d");
  // Finestra custom: giorni di calendario "YYYY-MM-DD", estremi inclusi.
  // Stringa vuota = nessun intervallo custom, vale il preset.
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // Granularita' RICHIESTA (`auto` = la risolve il backend sulla finestra).
  // Quella disegnata e' sempre `series.granularity`, cioe' la risolta.
  const [granularity, setGranularity] = useState<CostGranularity | "auto">("auto");
  const [tenantFilter, setTenantFilter] = useState("");
  const [flowFilter, setFlowFilter] = useState("");
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [summary, setSummary] = useState<CostSummary | null>(null);
  const [byTenant, setByTenant] = useState<CostByTenant[]>([]);
  const [byType, setByType] = useState<CostByType[]>([]);
  const [byStep, setByStep] = useState<CostByStep[]>([]);
  const [series, setSeries] = useState<CostTimeseries | null>(null);
  const [top, setTop] = useState<CostTopRequestsResponse | null>(null);
  const [topLimit, setTopLimit] = useState<number>(TOP_LIMITS[0]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const custom = Boolean(dateFrom && dateTo);

  // Giorni della finestra attiva: decidono quali granularita' si possono
  // chiedere (`hour` oltre 7 giorni e' un 400, non un troncamento).
  const activeDays = useMemo(
    () => (custom ? windowDays(dateFrom, dateTo) : daysOfPeriod(period)),
    [custom, dateFrom, dateTo, period],
  );
  const allowedGranularities = useMemo(() => granularityOptions(activeDays), [activeDays]);

  const loadTenants = useCallback(async () => {
    try { setTenants(await adminListTenants()); } catch { /* ignore */ }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Una sola finestra per tutte e cinque le rotte: KPI e tabelle fermi sul
      // preset mentre il trend segue l'intervallo custom sarebbe una pagina che
      // si contraddice da sola.
      const w: CostWindow = {
        ...(dateFrom && dateTo ? { dateFrom, dateTo } : { period }),
        tenantId: tenantFilter || undefined,
        flow: flowFilter || undefined,
      };
      const [s, bt, bty, bs, ts, tr] = await Promise.all([
        adminCostSummary(w),
        adminCostsByTenant(w),
        adminCostsByType(w),
        adminCostsByStep(w),
        adminCostsTimeseries({ ...w, granularity }),
        // Stessa finestra e stessi filtri delle altre cinque: una classifica
        // che parlasse di un altro periodo dei KPI sopra sarebbe la pagina che
        // si contraddice da sola.
        adminCostsTopRequests({ ...w, limit: topLimit }),
      ]);
      setSummary(s);
      setByTenant(bt.tenants);
      setByType(bty.types);
      setByStep(bs.steps);
      setSeries(ts);
      setTop(tr);
    } catch (err) {
      // Una finestra non caricabile AZZERA i riquadri. Tenere i dati della
      // finestra precedente accanto a un filtro che dichiara un altro periodo
      // e' peggio del vuoto: sono numeri veri, quindi credibili, ma riferiti a
      // giorni diversi da quelli scritti in cima — la dashboard che si
      // contraddice e' il difetto che questa pagina deve chiudere, non aprire.
      setSummary(null);
      setByTenant([]);
      setByType([]);
      setByStep([]);
      setSeries(null);
      setTop(null);
      setError(messaggioErrore(err));
    } finally {
      setLoading(false);
    }
  }, [period, dateFrom, dateTo, granularity, tenantFilter, flowFilter, topLimit]);

  useEffect(() => { loadTenants(); }, [loadTenants]);
  useEffect(() => { loadData(); }, [loadData]);

  /**
   * Serie caricata ma senza una sola chiamata: vale per `buckets: []` (una
   * finestra tutta nel futuro, che il backend restituisce vuota per scelta) e
   * per una finestra di bucket tutti a zero. In entrambi i casi i grafici
   * sarebbero riquadri vuoti senza spiegazione.
   */
  const serieSenzaChiamate = !error && series != null && series.buckets.every((b) => b.call_count === 0);

  // Una sola scala colore per torta e tabella: stesso step, stesso colore.
  const colorOf = useMemo(() => stepColorScale(byStep.map((s) => s.step)), [byStep]);

  // I flussi filtrabili sono quelli DAVVERO attivi per il cliente scelto
  // (`tenant.modules`), non una lista generica: casadei ha rdi+ddt+conversazioni,
  // un altro cliente ne ha altri. Senza un cliente specifico non c'e' un
  // insieme di moduli da mostrare (ogni cliente ha i suoi).
  const activeFlows = useMemo(() => {
    if (!tenantFilter) return [];
    const modules = tenants.find((t) => t.tenant_id === tenantFilter)?.modules ?? [];
    return modules.filter((m) => m in FLOW_LABELS);
  }, [tenantFilter, tenants]);

  /**
   * Un preset azzera l'intervallo custom: la finestra e' UNA, non due (il
   * client manda `period` **oppure** `date_from`+`date_to`). Se la
   * granularita' scelta a mano non e' ammessa dalla finestra nuova torna ad
   * `auto`, cosi' non si spedisce una richiesta che il backend rifiuta.
   */
  const applyPeriod = useCallback((value: string) => {
    setPeriod(value);
    setDateFrom("");
    setDateTo("");
    setGranularity((g) => (g === "auto" || granularityOptions(daysOfPeriod(value)).includes(g) ? g : "auto"));
  }, []);

  /**
   * Intervallo custom dal date-picker (che emette gia' "YYYY-MM-DD"): azzera
   * il preset. Il picker puo' consegnare il solo estremo iniziale (un giorno
   * cliccato e "Applica"): un giorno e' una finestra legittima di un giorno,
   * quindi si chiude su se stesso invece di spedire un intervallo a meta' —
   * che sarebbe un 400 in faccia all'utente per qualcosa che la UI gli ha
   * permesso di fare.
   */
  const applyRange = useCallback((from: string | null, to: string | null) => {
    if (!from) { setDateFrom(""); setDateTo(""); return; }
    const a = from;
    const b = to ?? from;
    setDateFrom(a);
    setDateTo(b);
    setGranularity((g) => (g === "auto" || granularityOptions(windowDays(a, b)).includes(g) ? g : "auto"));
  }, []);

  // Cambiare cliente invalida la scelta di flusso precedente (i moduli sono
  // diversi da cliente a cliente: un flusso valido per uno puo' non esistere
  // per l'altro).
  const handleTenantChange = useCallback((value: string) => {
    setTenantFilter(value);
    setFlowFilter("");
  }, []);

  // `tenant_id` null = chiamate non attribuibili a un cliente (migration 059):
  // e' una voce a se', non un tenant di nome "null".
  const tenantName = useCallback(
    (id: string | null) => id == null ? "Senza cliente" : (tenants.find((t) => t.tenant_id === id)?.name || id),
    [tenants],
  );

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">
          <span className="material-symbols-outlined" style={{ fontSize: 28, marginRight: 8 }}>payments</span>
          Consumi LLM
        </h1>
      </div>

      {/* Filtri */}
      <div style={{ display: "flex", gap: "var(--space-3)", marginBottom: "var(--space-5)", flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ display: "flex", gap: 4 }}>
          {PERIODS.map((p) => (
            <button
              key={p.value}
              className={`btn btn-sm ${!custom && period === p.value ? "btn-primary" : "btn-ghost"}`}
              onClick={() => applyPeriod(p.value)}
            >
              {p.label}
            </button>
          ))}
        </div>
        {/* Intervallo libero: il componente e' quello gia' in repo (emette
            "YYYY-MM-DD", ha "Applica" e le scorciatoie), nessuna dipendenza
            nuova. */}
        <DateRangePicker from={dateFrom || undefined} to={dateTo || undefined} onChange={applyRange} />
        {/* Ampiezza del bucket dei grafici. Le granularita' che il backend
            rifiuterebbe su questa finestra sono disabilitate, non nascoste:
            spariscono e riappaiono senza spiegazione sarebbe peggio. */}
        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
          <span className="text-sm text-muted">Dettaglio</span>
          {GRANULARITY_CHOICES.map((g) => {
            const disabled = g !== "auto" && !allowedGranularities.includes(g);
            return (
              <button
                key={g}
                type="button"
                disabled={disabled}
                className={`btn btn-sm ${granularity === g ? "btn-primary" : "btn-ghost"}`}
                title={
                  disabled
                    ? `Non disponibile su una finestra di ${fmtInt(activeDays)} giorni`
                    : g === "auto" && series
                      ? `Scelto dalla finestra: ${GRANULARITY_LABELS[series.granularity].toLowerCase()}`
                      : undefined
                }
                onClick={() => setGranularity(g)}
              >
                {GRANULARITY_LABELS[g]}
              </button>
            );
          })}
        </div>
        <select
          className="form-select"
          style={{ width: "auto", minWidth: 180 }}
          value={tenantFilter}
          onChange={(e) => handleTenantChange(e.target.value)}
        >
          <option value="">Tutti i clienti</option>
          {tenants.map((t) => (
            <option key={t.tenant_id} value={t.tenant_id}>{t.name}</option>
          ))}
        </select>
        {activeFlows.length > 0 && (
          <select
            className="form-select"
            style={{ width: "auto", minWidth: 160 }}
            value={flowFilter}
            onChange={(e) => setFlowFilter(e.target.value)}
            title="Filtra per flusso attivo di questo cliente"
          >
            <option value="">Tutti i flussi</option>
            {activeFlows.map((f) => (
              <option key={f} value={f}>{FLOW_LABELS[f]}</option>
            ))}
          </select>
        )}
      </div>

      {error && (
        <div className="card" style={{ borderLeft: "4px solid var(--indaco-danger)", marginBottom: "var(--space-4)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--indaco-danger)" }}>
            <span className="material-symbols-outlined">error</span>
            {error}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: "var(--space-8)" }}>
          <span className="spinner spinner-lg" />
        </div>
      ) : (
        <>
          {/* KPI cards + copertura del dato + finestra temporale */}
          {summary && (
            <>
              <PeriodWindowNote data={summary} />
              <SummaryCards data={summary} />
              <TicketCostComposition data={summary} />
              <CoverageNote data={summary} />
              <TicketSplitNote data={summary} />
            </>
          )}

          {/* Serie senza nemmeno una chiamata: lo si DICE. Una `.kpi-grid`
              senza figli (finestra nel futuro, o un periodo in cui il cliente
              non ha lavorato) lascia l'utente a indovinare se non c'e' niente
              o se la pagina e' rotta. */}
          {serieSenzaChiamate ? (
            <div className="card" style={{ display: "flex", alignItems: "flex-start", gap: "var(--space-2)", marginBottom: "var(--space-6)" }}>
              <span className="material-symbols-outlined" style={{ color: "var(--indaco-gray-500)" }}>info</span>
              <span>
                Nessuna chiamata LLM nel periodo selezionato: non c'è nulla da mostrare nei grafici.
                {series && series.date_from && series.date_to
                  ? ` Finestra: ${bucketTooltipLabel(series.date_from, "", "day")} – ${bucketTooltipLabel(series.date_to, "", "day")}.`
                  : ""}
              </span>
            </div>
          ) : (
            /* Trend + torta affiancati; `.kpi-grid` (auto-fit, min 180px) e' la
               sola griglia responsive del DS: collassa a una colonna sotto ~400px
               di contenuto invece di schiacciare la torta in 75px a 390px. */
            <div className="kpi-grid">
              {series && series.buckets.length > 0 && (
                <Section title={TREND_TITLES[series.granularity]} icon="trending_up" fill>
                  <TrendChart buckets={series.buckets} granularity={series.granularity} tenantName={tenantName} />
                </Section>
              )}
              {byStep.length > 0 && (
                <Section title="Costi per step" icon="account_tree" fill>
                  <StepPieChart steps={byStep} colorOf={colorOf} />
                </Section>
              )}
            </div>
          )}

          {/* Ticket piu' costosi — il verso "dal costo al ticket". Sta subito
              sotto i grafici perche' e' la domanda successiva a "quanto ho
              speso": *chi* l'ha speso. Ogni riga apre il dettaglio della
              pratica, dove si legge il perche' (chiamata per chiamata). */}
          {top && top.requests.length > 0 && (
            <Section title="Ticket più costosi" icon="confirmation_number">
              <TopRequestsTable
                data={top}
                limit={topLimit}
                onLimit={setTopLimit}
                tenantName={tenantName}
                timezone={summary?.timezone}
              />
            </Section>
          )}

          {/* Step table */}
          {byStep.length > 0 && (
            <Section title="Breakdown per step pipeline" icon="account_tree">
              <StepTable steps={byStep} colorOf={colorOf} />
            </Section>
          )}

          {/* By tenant — inutile quando e' gia' selezionato un solo cliente:
              sarebbe una tabella di una riga che ripete le card qui sopra. */}
          {!tenantFilter && byTenant.length > 0 && (
            <Section title="Per cliente" icon="apartment">
              <div className="card table-scroll" style={{ padding: 0 }}>
                <table className="truncate">
                  <thead>
                    <tr>
                      <th>Cliente</th>
                      <th style={{ textAlign: "right" }}>Chiamate</th>
                      <th style={{ textAlign: "right" }}>Token</th>
                      <th style={{ textAlign: "right" }}>Costo (€)</th>
                      <th style={{ textAlign: "right" }}>Latenza media</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byTenant.map((r) => (
                      <tr key={r.tenant_id ?? "__none__"}>
                        <td title={r.tenant_id ?? "Chiamate senza tenant nel contesto"}>{tenantName(r.tenant_id)}</td>
                        <td style={{ textAlign: "right" }}>{fmtInt(r.call_count)}</td>
                        <td style={{ textAlign: "right" }}>{fmtInt(r.total_tokens)}</td>
                        <td style={{ textAlign: "right" }}><CostCell row={r} /></td>
                        <td style={{ textAlign: "right" }}>{fmtMs(latencyOf(r))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          {/* By type */}
          {byType.length > 0 && (
            <Section title="Per tipologia e modello" icon="category">
              {series && <ModelTimelineChart buckets={series.buckets} granularity={series.granularity} />}
              <div className="card table-scroll" style={{ padding: 0 }}>
                <table className="truncate">
                  <thead>
                    <tr>
                      <th>Tipo</th>
                      <th>Modello</th>
                      <th style={{ textAlign: "right" }}>Chiamate</th>
                      <th style={{ textAlign: "right" }}>Token</th>
                      <th style={{ textAlign: "right" }}>Costo (€)</th>
                      <th style={{ textAlign: "right" }}>Latenza media</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byType.map((r, i) => (
                      <tr key={i}>
                        <td>{r.call_type}</td>
                        <td>{r.model == null ? <span className="text-muted" title="Modello mai registrato per queste chiamate">{NO_DATA}</span> : <code>{r.model}</code>}</td>
                        <td style={{ textAlign: "right" }}>{fmtInt(r.call_count)}</td>
                        <td style={{ textAlign: "right" }}>{fmtInt(r.total_tokens)}</td>
                        <td style={{ textAlign: "right" }}><CostCell row={r} /></td>
                        <td style={{ textAlign: "right" }}>{fmtMs(latencyOf(r))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}
        </>
      )}
    </div>
  );
}

/* ── Sub-components ── */

/*
 * Tabelle: `.card.table-scroll` scorre in orizzontale invece di clippare
 * (`overflow: hidden`) le colonne Costo/Latenza a 390px. `.truncate` sul
 * <table> e' l'unica utility nowrap del DS: qui conta solo il `white-space`
 * ereditato dalle celle (overflow/ellipsis sono inerti su un table box), cosi'
 * la tabella tiene la larghezza naturale e scorre, invece di spezzare parole e
 * numeri come impone `.card { overflow-wrap: anywhere }`.
 */

function CostCell({ row }: { row: { total_cost_usd: number | null } & CostCoverage }) {
  const unpriced = row.unpriced_call_count ?? 0;
  return (
    <>
      {fmtUsd(costOf(row))}
      {unpriced > 0 && (
        <span
          className="text-muted"
          style={{ marginLeft: 4, cursor: "help" }}
          title={`${fmtInt(unpriced)} chiamate senza prezzo, escluse dal totale`}
        >*</span>
      )}
    </>
  );
}

/**
 * Una riga di contesto sotto le card: quanto del periodo e' davvero coperto dal
 * totale. Se la copertura e' completa non mostra nulla — non e' una feature,
 * e' un promemoria che il buco esiste finche' non troviamo i prezzi mancanti.
 */
function CoverageNote({ data }: { data: CostSummary }) {
  const unpriced = data.unpriced_call_count ?? 0;
  const priced = data.priced_call_count ?? (unpriced ? data.call_count - unpriced : data.call_count);
  const latencySample = data.latency_sample_count;
  // Zero misure non e' "parziale": e' assente. Il footnote spiega solo il caso
  // in cui la media copre UNA PARTE delle chiamate, non il caso "nessuna"
  // (li' non c'e' una tile "Latenza media" da spiegare: vedi SummaryCards).
  const latencyPartial = latencySample != null && latencySample > 0 && latencySample < data.call_count;
  if (unpriced <= 0 && !latencyPartial) return null;

  return (
    <div
      className="text-sm text-muted"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "var(--space-2)",
        margin: "calc(-1 * var(--space-3)) 0 var(--space-5)",
        lineHeight: 1.4,
      }}
    >
      <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--indaco-warning)" }}>info</span>
      <span>
        {unpriced > 0 && (
          <>
            Il costo totale copre {fmtInt(priced)} chiamate su {fmtInt(data.call_count)}: per {fmtInt(unpriced)} manca il prezzo del modello
            {data.unpriced_tokens ? ` (${fmtInt(data.unpriced_tokens)} token non valorizzati)` : ""}.{" "}
          </>
        )}
        {latencyPartial && (
          <>Latenza media calcolata su {fmtInt(latencySample as number)} chiamate con latenza registrata.</>
        )}
      </span>
    </div>
  );
}

/**
 * Il costo che NON appartiene a un ticket, dichiarato.
 *
 * `llm_calls.request_id` e' nullable: i turni dell'agente conversazionale (e
 * qualunque chiamata fatta fuori da una pratica) non hanno un ticket. Quel
 * costo non si nasconde e non si spalma sui ticket — spalmarlo alzerebbe il
 * costo unitario di un numero che nessuno ha misurato, nasconderlo farebbe
 * sembrare che il totale sia tutto lavoro su pratiche. Se e' zero questa riga
 * non compare: e' una dichiarazione, non una decorazione.
 */
function TicketSplitNote({ data }: { data: CostSummary }) {
  const senzaTicket = data.unattributed_call_count ?? 0;
  // La media e' un limite inferiore quando parte delle chiamate dei ticket non
  // ha prezzo: dirlo qui, accanto alla tile, non in un tooltip.
  const ticketNonPrezzate = data.ticket_call_count - data.ticket_priced_call_count;
  if (senzaTicket <= 0 && ticketNonPrezzate <= 0) return null;

  return (
    <div
      className="text-sm text-muted"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "var(--space-2)",
        margin: "calc(-1 * var(--space-3)) 0 var(--space-5)",
        lineHeight: 1.4,
      }}
    >
      <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--indaco-gray-500)" }}>call_split</span>
      <span>
        {senzaTicket > 0 && (
          <>
            {fmtUsd(data.unattributed_cost_usd)} su {fmtInt(senzaTicket)} chiamate
            {data.unattributed_tokens ? ` (${fmtInt(data.unattributed_tokens)} token)` : ""}
            {" "}non sono attribuibili a un ticket — chiamate senza <code>request_id</code>, oggi
            i turni dell'agente conversazionale. Sono nel costo totale, ma fuori dal costo medio
            per ticket e dalla classifica: spalmarle sui ticket sarebbe un costo unitario inventato.{" "}
          </>
        )}
        {ticketNonPrezzate > 0 && (
          <>
            Il costo attribuito copre {fmtInt(data.ticket_priced_call_count)} chiamate di ticket
            su {fmtInt(data.ticket_call_count)}: la media per ticket è quindi un minimo.
          </>
        )}
      </span>
    </div>
  );
}

/** Il periodo e' fatto di giorni di calendario in un fuso preciso: dillo. */
function PeriodWindowNote({ data }: { data: CostSummary }) {
  if (!data.period_start && !data.period_end && !data.timezone) return null;
  const range = data.period_start && data.period_end
    ? `${fmtDay(data.period_start, data.timezone)} – ${fmtDay(data.period_end, data.timezone)}`
    : null;
  return (
    <div className="text-sm text-muted" style={{ marginBottom: "var(--space-3)" }}>
      {range}
      {range && data.timezone ? " · " : ""}
      {data.timezone ? `giorni di calendario, fuso ${data.timezone}` : ""}
    </div>
  );
}

/**
 * Costo medio per ticket in tile KPI. Resta a 2 decimali come gli altri costi
 * (la regola della pagina: niente notazione compatta, niente precisione che
 * cambia da tile a tile), ma il valore esatto va nel `title`: su un costo
 * unitario di pochi centesimi la terza cifra e' proprio quella che dice se un
 * mese e' andato peggio del precedente, e nasconderla del tutto sarebbe
 * togliere l'informazione per cui la tile esiste.
 */
function fmtKpiMediaTicket(data: CostSummary): { text: string; title?: string } {
  const media = data.avg_cost_per_ticket_usd;
  if (media == null) return { text: NO_DATA, title: "Nessun ticket con costo noto nella finestra" };
  // La composizione entra nel `title` invece di diventare tile: la riga sotto
  // i KPI (`TicketCostComposition`) la mostra in chiaro, qui serve a chi passa
  // sopra alla media senza scendere.
  const composizione = shareInOrdine(data.ticket_cost_by_category ?? [])
    .map((v) => `${categoryLabel(v.category)} ${fmtUsd(costOfShare(v))}`)
    .join(" · ");
  return {
    text: fmtUsd(media),
    title:
      `€${_fmtEur(6).format(media)} per ticket — ${fmtUsd(data.ticket_cost_usd)} ` +
      `attribuiti a ${fmtInt(data.ticket_count)} ticket` +
      (data.unattributed_cost_usd != null
        ? `. Esclude ${fmtUsd(data.unattributed_cost_usd)} non attribuibili a un ticket.`
        : ".") +
      (composizione ? ` Composizione: ${composizione}.` : ""),
  };
}

function SummaryCards({ data }: { data: CostSummary }) {
  const cards = [
    { icon: "api", label: "Chiamate LLM", ...fmtKpiInt(data.call_count) },
    // Il denominatore del costo unitario. Uno zero qui e' una misura vera
    // ("nessuna pratica ha consumato in questa finestra"), quindi la tile c'e'
    // sempre — al contrario della media, che senza ticket non esiste.
    {
      icon: "confirmation_number",
      label: "Ticket lavorati",
      ...fmtKpiInt(data.ticket_count),
      title: `${fmtInt(data.ticket_count)} pratiche distinte con almeno una chiamata LLM nella finestra (${fmtInt(data.ticket_call_count)} chiamate su ${fmtInt(data.call_count)}). Non è il numero di pratiche nate nel periodo: una pratica riprocessata conta nella finestra in cui consuma.`,
    },
    { icon: "token", label: "Token totali", ...fmtKpiInt(data.total_tokens) },
    { icon: "attach_money", label: "Costo totale", ...fmtKpiUsd(costOf(data)) },
    // La media compare solo se c'e' un ticket: una media su zero ticket non e'
    // "€0,00", e' un conto che non si puo' fare (stessa regola della latenza).
    ...(data.ticket_count > 0
      ? [{ icon: "receipt_long", label: "Costo medio/ticket", ...fmtKpiMediaTicket(data) }]
      : []),
    // Nessuna chiamata con latenza registrata: un trattino con la spiegazione
    // "su 0 chiamate" non e' un dato, e' rumore. La tile compare solo se
    // esiste almeno una misura (anche se non copre tutte le chiamate: in quel
    // caso resta la media parziale, spiegata da CoverageNote).
    ...((data.latency_sample_count ?? 0) > 0
      ? [{ icon: "speed", label: "Latenza media", text: fmtMs(latencyOf(data)) }]
      : []),
  ];
  return (
    <div className="kpi-grid">
      {cards.map((c) => (
        <div className="card kpi-tile" key={c.label}>
          <span className="kpi-icon material-symbols-outlined" style={{ color: "var(--indaco-brand-primary)" }}>{c.icon}</span>
          <div>
            <div className="kpi-value" title={c.title}>{c.text}</div>
            <div className="kpi-label">{c.label}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * `fill`: quando due Section stanno affiancate nella stessa riga di
 * `.kpi-grid` (il trend + "Costi per step"), la griglia allarga la riga
 * all'altezza della piu' alta, ma il `.card` dentro ognuna NON si allunga da
 * solo (e' block, non flex) — resta alto quanto il suo contenuto, lasciando
 * un vuoto invisibile sotto quello piu' corto. `fill` rende la Section stessa
 * flex-colonna e il suo `.card` un flex item che cresce fino a riempirla —
 * innocuo fuori da un genitore flex/grid (dove `flex` non ha effetto), quindi
 * non tocca le altre Section a piena larghezza che non ne hanno bisogno.
 *
 * L'altezza della riga NON viene piu' da un grafico: la porta `.chart-body`
 * (token `--chart-row-height`), e `.section-fill` si affida allo stretch
 * nativo del grid item — che, al contrario di `height: 100%`, e' gia' al
 * netto del `margin-bottom` qui sotto. Vedi il commento in `indaco.css`:
 * quei 24px di margine non riscalati erano l'intera causa dei ~24px di
 * inchiostro sfalsato fra le due card.
 */
function Section({ title, icon, children, fill }: { title: string; icon: string; children: React.ReactNode; fill?: boolean }) {
  return (
    <div className={fill ? "section-fill" : undefined} style={{ marginBottom: "var(--space-6)" }}>
      <h2 style={{ fontSize: "var(--font-size-lg)", fontWeight: 600, display: "flex", alignItems: "center", gap: 6, marginBottom: "var(--space-3)" }}>
        <span className="material-symbols-outlined" style={{ fontSize: 22, color: "var(--indaco-brand-primary)" }}>{icon}</span>
        {title}
      </h2>
      {children}
    </div>
  );
}

const NO_TENANT_KEY = "__nessun_cliente__";

/** Chiave di pivot stabile per un tenant_id (recharts vuole chiavi di oggetto, non null). */
function tenantKey(tenantId: string | null): string {
  return tenantId ?? NO_TENANT_KEY;
}

/** Stessa idea di `stepColorScale` (costSteps.ts) ma generica sulla chiave: usata
 * sia per i tenant del trend sia per i modelli della timeline —
 * un colore per chiave, assegnato nell'ordine di prima comparsa nei dati. */
function paletteScale(keys: string[]): (key: string) => string {
  const assigned = new Map<string, string>();
  for (const k of keys) {
    if (!assigned.has(k)) assigned.set(k, CHART_PALETTE[assigned.size % CHART_PALETTE.length]);
  }
  return (key) => assigned.get(key) ?? "var(--indaco-gray-600)";
}

/** Oltre questo numero di bucket il grafico offre il `<Brush>`: sotto si
 *  vedono tutti e non c'e' nulla da restringere. */
const BRUSH_FROM_BUCKETS = 12;

/**
 * Trend dei costi sulla serie a bucket adattivi (`/timeseries`): un giorno di
 * finestra da' barre ORARIE, una settimana barre giornaliere, un anno barre
 * settimanali, piu' anni barre MENSILI. Le etichette (tick e tooltip) vengono
 * da `costBuckets`, che le sa fare per tutte e quattro le unita': qui non si
 * formatta nessuna data a mano.
 */
/**
 * Bucket della serie -> righe per recharts, e insieme i tenant da impilare.
 *
 * Funzione pura, ed esportata **perche' deve essere sotto test**: e' qui che
 * vive l'invariante dei costi lato UI, cioe' che un bucket senza chiamate
 * prezzate **non porta la chiave** del costo. Recharts allora non disegna
 * nulla, e un'ora vuota resta un'ora vuota; scriverci `0` la mostrerebbe come
 * un'ora *gratis*, che e' una misura che nessuno ha fatto (il backend manda
 * `cost_usd: null` proprio per non dirlo). Il tick sull'asse resta comunque,
 * quindi il buco si vede.
 *
 * `multiTenant` e' vero solo con piu' di un cliente nella finestra: con uno
 * solo la barra resta piena e senza legenda.
 */
export function buildTrendData(buckets: CostBucket[]): {
  tenantIds: string[];
  multiTenant: boolean;
  data: Record<string, string | number | null>[];
} {
  // Tenant distinti sull'intera finestra, nell'ordine di prima comparsa.
  const tenantIds: string[] = [];
  const seen = new Set<string>();
  for (const b of buckets) {
    for (const t of b.by_tenant) {
      const key = tenantKey(t.tenant_id);
      if (!seen.has(key)) { seen.add(key); tenantIds.push(key); }
    }
  }
  const multiTenant = tenantIds.length > 1;

  const data = buckets.map((b) => {
    const row: Record<string, string | number | null> = { bucket_start: b.bucket_start };
    if (!multiTenant) {
      // `priced_call_count === 0` e' la cintura sul costo aggregato: nessuna
      // chiamata prezzata = costo ignoto, non zero.
      const costo = b.priced_call_count === 0 ? null : b.cost_usd;
      if (costo != null) row.cost_usd = costo;
      return row;
    }
    for (const t of b.by_tenant) {
      if (t.cost_usd != null) row[tenantKey(t.tenant_id)] = t.cost_usd;
    }
    return row;
  });

  return { tenantIds, multiTenant, data };
}

function TrendChart({ buckets, granularity, tenantName }: {
  buckets: CostBucket[];
  granularity: CostGranularity;
  tenantName: (id: string | null) => string;
}) {
  const { tenantIds, multiTenant, data } = buildTrendData(buckets);
  const colorOf = paletteScale(tenantIds);

  // `bucket_start` -> `bucket_end`, per l'etichetta del tooltip: la fine di un
  // bucket orario la sa il backend (il 25 ottobre un bucket dura due ore
  // reali), non un "+1" calcolato qui.
  const endOf = new Map<string, string>(buckets.map((b) => [b.bucket_start, b.bucket_end]));

  return (
    <div className="card">
      {/* L'altezza la porta `.chart-body` (token `--chart-row-height`), non un
          numero dentro il grafico: e' cosi' che questa card e la torta
          restano alte uguale senza che una faccia da ancora all'altra. */}
      <div className="chart-body">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--indaco-gray-200)" />
            <XAxis
              dataKey="bucket_start"
              tick={{ fontSize: 11, fill: "var(--indaco-gray-600)" }}
              tickFormatter={(v: string) => bucketTickLabel(String(v), granularity)}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 11, fill: "var(--indaco-gray-600)" }}
              tickFormatter={(v: number) => fmtUsd(v)}
              width={60}
            />
            <Tooltip
              contentStyle={{ fontSize: 13, borderRadius: 6, border: "1px solid var(--indaco-gray-300)" }}
              formatter={(value, name) => [
                value == null ? NO_DATA : fmtUsd(Number(value)),
                multiTenant ? tenantName(name === NO_TENANT_KEY ? null : String(name)) : "Costo",
              ]}
              labelFormatter={(label) => bucketTooltipLabel(String(label), endOf.get(String(label)) ?? "", granularity)}
            />
            {multiTenant && (
              <Legend
                formatter={(value: string) => tenantName(value === NO_TENANT_KEY ? null : value)}
                wrapperStyle={{ fontSize: 12 }}
              />
            )}
            {multiTenant ? (
              tenantIds.map((key, i) => (
                <Bar
                  key={key}
                  dataKey={key}
                  name={key}
                  stackId="cost"
                  fill={colorOf(key)}
                  radius={i === tenantIds.length - 1 ? [3, 3, 0, 0] : undefined}
                  maxBarSize={32}
                />
              ))
            ) : (
              <Bar dataKey="cost_usd" fill="var(--indaco-brand-primary)" radius={[3, 3, 0, 0]} maxBarSize={32} />
            )}
            {/* Lo "slider" di zoom: restringe la vista ai bucket selezionati
                senza rifare la fetch (i dati ci sono gia' tutti). Vive dentro
                l'altezza condivisa della riga, quindi non riapre il problema
                dell'allineamento. */}
            {buckets.length > BRUSH_FROM_BUCKETS && (
              <Brush
                dataKey="bucket_start"
                height={18}
                travellerWidth={8}
                stroke="var(--indaco-gray-400)"
                fill="var(--indaco-gray-100)"
                tickFormatter={(v) => bucketTickLabel(String(v), granularity)}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const NO_MODEL_KEY = "__modello_ignoto__";

/**
 * "Roadmap" di quale modello e' stato usato bucket per bucket — la tabella
 * sotto ("Per tipologia e modello") dice il totale del periodo, ma un cliente
 * che ha CAMBIATO modello a meta' periodo ci apparirebbe come se avesse
 * sempre usato solo l'ultimo. Si mostra solo se c'e' davvero un cambio da
 * vedere (un solo modello in tutta la finestra non ha nulla da "mappare").
 *
 * Usa gli stessi bucket e le stesse etichette del trend: su una finestra di un
 * giorno anche qui le ascisse sono ore, non date ripetute.
 */
function ModelTimelineChart({ buckets, granularity }: { buckets: CostBucket[]; granularity: CostGranularity }) {
  const modelKeys: string[] = [];
  const seen = new Set<string>();
  for (const b of buckets) {
    for (const m of b.by_model) {
      const key = m.model ?? NO_MODEL_KEY;
      if (!seen.has(key)) { seen.add(key); modelKeys.push(key); }
    }
  }
  if (modelKeys.length <= 1) return null;

  const colorOf = paletteScale(modelKeys);
  const modelLabel = (key: string) => (key === NO_MODEL_KEY ? "Modello ignoto" : key);
  const endOf = new Map<string, string>(buckets.map((b) => [b.bucket_start, b.bucket_end]));

  const data = buckets.map((b) => {
    const row: Record<string, string | number | null> = { bucket_start: b.bucket_start };
    for (const m of b.by_model) {
      if (m.call_count > 0) row[m.model ?? NO_MODEL_KEY] = m.call_count;
    }
    return row;
  });

  return (
    <div className="card" style={{ marginBottom: "var(--space-4)" }}>
      <div className="text-sm text-muted" style={{ marginBottom: "var(--space-2)" }}>
        Quale modello è stato usato ogni {BUCKET_UNIT[granularity]} (numero di chiamate)
      </div>
      <div className="chart-body-sm">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--indaco-gray-200)" />
            <XAxis
              dataKey="bucket_start"
              tick={{ fontSize: 11, fill: "var(--indaco-gray-600)" }}
              tickFormatter={(v: string) => bucketTickLabel(String(v), granularity)}
              interval="preserveStartEnd"
            />
            <YAxis tick={{ fontSize: 11, fill: "var(--indaco-gray-600)" }} width={40} allowDecimals={false} />
            <Tooltip
              contentStyle={{ fontSize: 13, borderRadius: 6, border: "1px solid var(--indaco-gray-300)" }}
              formatter={(value, name) => [value == null ? NO_DATA : fmtInt(Number(value)), modelLabel(String(name))]}
              labelFormatter={(label) => bucketTooltipLabel(String(label), endOf.get(String(label)) ?? "", granularity)}
            />
            <Legend formatter={(value: string) => modelLabel(value)} wrapperStyle={{ fontSize: 12 }} />
            {modelKeys.map((key, i) => (
              <Bar
                key={key}
                dataKey={key}
                name={key}
                stackId="calls"
                fill={colorOf(key)}
                radius={i === modelKeys.length - 1 ? [3, 3, 0, 0] : undefined}
                maxBarSize={24}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function StepPieChart({ steps, colorOf }: { steps: CostByStep[]; colorOf: (step: string) => string }) {
  const totalCost = sumCost(steps) || 1;
  const data = steps
    .map((s) => ({ step: s.step, cost: costOf(s) }))
    .filter((s): s is { step: string; cost: number } => s.cost != null)
    // La percentuale sta nel nome: finisce in legenda (e nel tooltip) senza
    // etichette esterne, che a 291px di colonna uscivano dalla card di ~83px.
    .map((s) => ({
      name: `${stepLabel(s.step)} · ${fmtPct((s.cost / totalCost) * 100)}`,
      value: s.cost,
      fill: colorOf(s.step),
    }));

  return (
    <div className="card">
      {/* Stessa `.chart-body` del trend: l'inchiostro parte e finisce alla
          stessa altezza nelle due card. Il vecchio `height={280}` inline era
          la sola altezza intrinseca della riga, e la centratura flex
          trasformava i 24px di troppo in 12 sopra e 12 sotto la ciambella. */}
      <div className="chart-body">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            {/* Raggi in percentuale dell'area utile (gia' al netto della legenda):
                la torta si adatta alla colonna invece di sforare a raggio fisso. */}
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius="42%"
              outerRadius="75%"
              paddingAngle={2}
            >
              {data.map((d, i) => (
                <Cell key={i} fill={d.fill} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ fontSize: 13, borderRadius: 6, border: "1px solid var(--indaco-gray-300)" }}
              formatter={(value) => [fmtUsd(Number(value)), "Costo"]}
            />
            <Legend
              iconType="circle"
              iconSize={10}
              wrapperStyle={{ fontSize: 12 }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/** Id del ticket accorciato per la tabella: il valore intero sta nel `title`
 *  e nel link, qui basta riconoscere due righe diverse. */
function idBreve(id: string): string {
  return id.length > 12 ? `${id.slice(0, 8)}…${id.slice(-3)}` : id;
}

/**
 * Classifica dei ticket piu' costosi della finestra.
 *
 * Serve a una domanda sola — "se qualcosa costa di piu' vado a vedere
 * perche'" — quindi ogni riga e' un varco verso il dettaglio della pratica,
 * dove il costo e' gia' spaccato chiamata per chiamata (`/by-request`, usata da
 * `RequestDetail`). Il contesto in tabella (cliente, flusso, stato, data,
 * messaggio + `ticket_index`) serve a decidere QUALE riga aprire senza aprirle
 * tutte.
 *
 * Una riga con `request_found: false` non e' cliccabile: `request_id` non e' una
 * FK e per il flusso deleghe porta l'id di un altro dominio. Il costo si mostra
 * comunque (e' stato speso), ma non si finge un dettaglio che darebbe 404.
 */
function TopRequestsTable({ data, limit, onLimit, tenantName, timezone }: {
  data: CostTopRequestsResponse;
  limit: number;
  onLimit: (n: number) => void;
  tenantName: (id: string | null) => string;
  timezone?: string;
}) {
  const navigate = useNavigate();
  const mostrati = data.requests.length;
  // Le categorie che compaiono DAVVERO nelle righe mostrate: la legenda
  // spiega i colori una volta sopra la tabella invece di ripetere l'etichetta
  // in ogni cella.
  const categorie = [...new Set(data.requests.flatMap((r) => (r.cost_by_category ?? []).map((v) => v.category)))];

  return (
    <>
      <div style={{ display: "flex", alignItems: "baseline", gap: "var(--space-3)", flexWrap: "wrap", marginBottom: "var(--space-2)" }}>
        {/* Quanti ticket la tabella NON sta mostrando: senza questo numero
            "i primi 10" si legge come "sono 10". */}
        <span className="text-sm text-muted">
          {mostrati < data.ticket_count
            ? `I ${fmtInt(mostrati)} più costosi sui ${fmtInt(data.ticket_count)} ticket della finestra`
            : `Tutti i ${fmtInt(data.ticket_count)} ticket della finestra`}
        </span>
        <ComposizioneLegenda categorie={categorie} />
        <div style={{ display: "flex", gap: 4, alignItems: "center", marginLeft: "auto" }}>
          <span className="text-sm text-muted">Righe</span>
          {TOP_LIMITS.map((n) => (
            <button
              key={n}
              type="button"
              className={`btn btn-sm ${limit === n ? "btn-primary" : "btn-ghost"}`}
              onClick={() => onLimit(n)}
            >
              {n}
            </button>
          ))}
        </div>
      </div>
      <div className="card table-scroll" style={{ padding: 0 }}>
        <table className="truncate">
          <thead>
            <tr>
              <th>Ticket</th>
              <th>Cliente</th>
              <th>Flusso</th>
              <th>Stato</th>
              <th>Data</th>
              <th style={{ textAlign: "right" }}>Chiamate</th>
              <th style={{ textAlign: "right" }}>Token</th>
              <th style={{ textAlign: "right" }}>Costo (€)</th>
              {/* Ultima colonna, come la barra della tabella step: e' il
                  dettaglio del numero che la precede, e larga perche' porta
                  barra e cifre. */}
              <th style={{ width: "24%" }} title="Di cosa è fatto il costo: il dialogo che ha generato la pratica, l'acquisizione (OCR/trascrizione), l'analisi, il resto della lavorazione. La somma delle voci è il costo della riga.">
                Composizione
              </th>
            </tr>
          </thead>
          <tbody>
            {data.requests.map((r) => (
              <TopRequestRow
                key={r.request_id}
                r={r}
                tenantName={tenantName}
                timezone={timezone}
                onOpen={r.request_found ? () => navigate(`/requests/${r.request_id}`) : undefined}
              />
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function TopRequestRow({ r, tenantName, timezone, onOpen }: {
  r: CostTopRequest;
  tenantName: (id: string | null) => string;
  timezone?: string;
  onOpen?: () => void;
}) {
  // Riferimento al messaggio: un messaggio email porta fino a 8 ticket, quindi
  // "3/8" non e' un dettaglio ma cio' che distingue due righe con lo stesso
  // `message_id`.
  const dettaglioTicket = r.total_tickets != null && r.total_tickets > 1 && r.ticket_index != null
    ? `ticket ${r.ticket_index + 1}/${r.total_tickets}`
    : null;
  const titoloMessaggio = [r.channel, r.message_id, dettaglioTicket].filter(Boolean).join(" · ");

  return (
    <tr
      onClick={onOpen}
      style={onOpen ? { cursor: "pointer" } : undefined}
      title={onOpen ? "Apri il dettaglio della pratica" : undefined}
    >
      <td>
        {onOpen ? (
          <Link to={`/requests/${r.request_id}`} onClick={(e) => e.stopPropagation()} title={r.request_id}>
            <code>{idBreve(r.request_id)}</code>
          </Link>
        ) : (
          <code
            className="text-muted"
            title={`${r.request_id} — questo id non è una pratica (per il flusso Deleghe è un controllo di delega): nessun dettaglio da aprire`}
          >
            {idBreve(r.request_id)}
          </code>
        )}
        {titoloMessaggio && (
          <div className="text-xs text-muted" title={titoloMessaggio}>
            {dettaglioTicket ?? r.channel ?? ""}
          </div>
        )}
      </td>
      <td title={r.tenant_id ?? "Chiamate senza tenant nel contesto"}>{tenantName(r.tenant_id)}</td>
      <td title={r.steps.length ? `Step: ${r.steps.join(", ")}` : undefined}>
        {r.flow && FLOW_LABELS[r.flow]
          ? FLOW_LABELS[r.flow]
          : <span className="text-muted">{NO_DATA}</span>}
      </td>
      <td>{r.status ? <StatusBadge status={r.status} /> : <span className="text-muted">{NO_DATA}</span>}</td>
      <td title={r.created_at ?? undefined}>
        {r.created_at ? fmtDay(r.created_at, timezone) : <span className="text-muted">{NO_DATA}</span>}
      </td>
      <td style={{ textAlign: "right" }}>{fmtInt(r.call_count)}</td>
      <td style={{ textAlign: "right" }}>{fmtInt(r.total_tokens)}</td>
      {/* La colonna resta a 2 decimali come ogni altro costo della pagina, ma
          in una classifica due righe vicine possono valere "€0,09" entrambe e
          l'ordinamento sembrerebbe rotto: il valore esatto sta nel tooltip. */}
      <td
        style={{ textAlign: "right" }}
        title={r.total_cost_usd != null ? `€${_fmtEur(6).format(r.total_cost_usd)}` : undefined}
      >
        <CostCell row={r} />
      </td>
      <td>
        <ComposizioneBar voci={r.cost_by_category ?? []} />
      </td>
    </tr>
  );
}

function StepTable({ steps, colorOf }: { steps: CostByStep[]; colorOf: (step: string) => string }) {
  const totalCost = sumCost(steps) || 1;

  return (
    <div className="card table-scroll" style={{ padding: 0 }}>
      <table className="truncate">
        <thead>
          <tr>
            <th>Step</th>
            <th style={{ textAlign: "right" }}>Chiamate</th>
            <th style={{ textAlign: "right" }}>Token</th>
            <th style={{ textAlign: "right" }}>Costo (€)</th>
            <th style={{ textAlign: "right" }}>%</th>
            <th style={{ textAlign: "right" }}>Latenza media</th>
            <th style={{ width: "20%" }}></th>
          </tr>
        </thead>
        <tbody>
          {steps.map((r) => {
            const cost = costOf(r);
            const pct = ((cost ?? 0) / totalCost) * 100;
            return (
              <tr key={r.step}>
                <td>
                  <span style={{
                    display: "inline-block",
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: colorOf(r.step),
                    marginRight: 8,
                    verticalAlign: "middle",
                  }} />
                  <strong>{stepLabel(r.step)}</strong>
                </td>
                <td style={{ textAlign: "right" }}>{fmtInt(r.call_count)}</td>
                <td style={{ textAlign: "right" }}>{fmtInt(r.total_tokens)}</td>
                <td style={{ textAlign: "right" }}><CostCell row={r} /></td>
                <td style={{ textAlign: "right" }}>{cost == null ? NO_DATA : fmtPct(pct)}</td>
                <td style={{ textAlign: "right" }}>{fmtMs(latencyOf(r))}</td>
                <td>
                  <div className="progress-bar">
                    <div className="progress-bar-fill" style={{ width: `${pct}%`, background: colorOf(r.step) }} />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
